import { APP_ORIGIN } from './appOrigin';

/**
 * Custom domain (owner rulings 2026-10-18): the app lives at
 * https://sspauditplus.selfstorageplus.com and the old Static Web Apps
 * address forwards there, so old email links and bookmarks keep working.
 *
 * Switched on by the build variable VITE_CANONICAL_ORIGIN (GitHub repo
 * variable), set only once the new address is validated and registered as a
 * sign-in redirect URI — so merging this code changes nothing by itself.
 *
 * Two things must NOT move with a blind redirect, because browsers keep them
 * per address:
 *  - a sign-in response (#code=… / #error=…, or ?code=… if the response
 *    mode ever changes): MSAL's matching request state lives in this
 *    address's storage, so it finishes here (MSAL then reloads the page
 *    without it, and that load moves);
 *  - audit work not yet synced (IndexedDB): moving would strand it. The page
 *    then stays here with a banner until the sync engine has sent it — and
 *    if this device's storage can't be read (or answers too slowly), it is
 *    treated as possibly holding work, never as empty.
 */

/** The app's address — the link base baked into every flow email (provisioning/flows). */
export { APP_ORIGIN };

/** Addresses that forward to the canonical one: the production SWA default host only (never staging). */
export const LEGACY_HOSTS: readonly string[] = ['lively-stone-01284d20f.7.azurestaticapps.net'];

/** The configured canonical origin (https only, no path), or null when the switch is off. */
export function canonicalOrigin(raw = import.meta.env.VITE_CANONICAL_ORIGIN): string | null {
  const v = String(raw ?? '').trim().replace(/\/+$/, '');
  if (!v) return null;
  try {
    const u = new URL(v);
    if (u.protocol !== 'https:' || u.origin !== v.toLowerCase()) return null;
    return u.origin;
  } catch {
    return null;
  }
}

type Place = Pick<Location, 'hostname' | 'origin' | 'pathname' | 'search' | 'hash'>;

/** Where this page should move to, or null to stay. */
export function movedTarget(here: Place, canonical: string | null): string | null {
  if (!canonical) return null;
  if (!LEGACY_HOSTS.includes(here.hostname.toLowerCase())) return null;
  if (here.origin === canonical) return null;
  if (/(^[#?]|&)(code|error)=/.test(here.hash) || /(^[#?]|&)(code|error)=/.test(here.search)) return null;
  return canonical + here.pathname + here.search + here.hash;
}

export interface PendingMove {
  target: string;
  /** This device's saved work couldn't be checked (storage unreadable or too slow). */
  unknown: boolean;
}

let pendingMove: PendingMove | null = null;

/** Set at start-up when the page should move but may hold unsynced work (src/main.tsx). */
export function setPendingMove(move: PendingMove | null): void {
  pendingMove = move;
}

/** The move the "AuditPlus has moved" banner offers, or null. */
export function getPendingMove(): PendingMove | null {
  return pendingMove;
}

/** How long start-up waits for the local store before showing the app + banner instead. */
export const PENDING_CHECK_TIMEOUT_MS = 4000;
